import type { ActivityKind } from '@/pages/ats-job-creation/tabs/interview-plan/stage/automation/types';

// Default delay between move-commit and `/workflow_executions/.../
// trigger`. Window during which the user can hover-to-pause and Stop.
export const UNDERWAY_DELAY_MS = 15_000;

export type UnderwayCandidate = {
  // The candidate's APPLICATION id — the row's `jobId` + this id build the
  // application deep-link (see `candidateApplicationUrl.ts`).
  id: string;
  name: string;
  avatarUrl?: string;
};

// A reviewer (review activity) or hiring-manager user (employee survey) the
// activity is sent to — named in the single-candidate copy, counted in bulk.
export type UnderwayRecipient = {
  name: string;
  avatarUrl?: string;
};

// Kind-specific context the per-activity copy needs beyond kind/label.
// Populated from the execution's `next_action` (see workflowExecutions.api).
export type UnderwayActivityDetails = {
  // Interview scheduling method — BE `automation_mode`
  // (self_schedule / request_availability / ai_coordinator).
  scheduleMode?: string;
  // Survey audience — separates the candidate survey ("to the candidate")
  // from the HM/employee survey ("on the candidate to N users").
  surveyType?: 'candidate' | 'employee';
  // Reviewers (review) or hiring-manager users (employee survey).
  recipients?: UnderwayRecipient[];
  recipientKind?: 'reviewer' | 'user';
};

export type UnderwayStatus =
  | 'pending'
  | 'paused'
  | 'running'
  | 'completed'
  | 'failed'
  | 'cancelled';

export type UnderwayActivity = {
  id: string;
  activityKind: ActivityKind | string;
  activityLabel: string;
  stageId: string;
  stageName: string;
  jobId: string;
  jobName: string;
  candidates: UnderwayCandidate[];
  isBulk: boolean;
  executionIds: number[];
  // When the move that triggered this row was INITIATED — the list is ordered
  // by this (newest first) so an async bulk row (enqueued only once its
  // dispatch session resolves) still lands in move order, not resolution order.
  // Falls back to `createdAt` when absent (older persisted rows).
  initiatedAt?: number;
  // When the row was ENQUEUED (row appeared) — drives the 15s undo countdown
  // and is reset on pause/resume, so it can't double as the sort key.
  createdAt: number;
  // When the row transitioned to `running` (its automation actually started
  // executing). Within the active group rows sort by this (most-recently-started
  // first), falling back to `initiatedAt` for rows that haven't run yet.
  startedRunningAt?: number;
  delayMs: number;
  remainingMs: number;
  pausedAt: number | null;
  status: UnderwayStatus;
  errorMessage?: string;
  // Kind-specific copy context (interview mode, survey audience, recipients).
  details?: UnderwayActivityDetails;
  // True when the first step is BE-delayed. The row still runs the 15s undo →
  // trigger like any other, but the BE re-schedules the action for `delaySeconds`
  // (see `ExecuteWorker`), so the copy reads "scheduled" / "Sends in X" rather
  // than "sent". Purely a copy signal — no effect on the timer/trigger.
  delayed?: boolean;
  // First-step delay in seconds, for the "Sends in X" line on a delayed row.
  delaySeconds?: number;
};

export type UnderwayViewMode = 'expanded' | 'minimized' | 'hidden';
